import { Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { addDays } from "date-fns";
import { useEffect, useMemo } from "react";

import { Button } from "../../components/Button";
import { InAppAdCard } from "../../Shift/InAppAd/Card";
import { type InferedInAppAdAttributesSchema } from "../../Shift/Open/types";
import { WorkerPaySurveyCard } from "../../WorkerPaySurvey/components/WorkerPaySurveyCard";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../Filters/constants";
import { useGetShiftsStats } from "../NearbyBookingStats/api/useGetShiftsStats";
import { getBookingStatsCardProps } from "../NearbyBookingStats/getBookingStatsCardProps";
import { getEmptyStateHeadline } from "../NearbyBookingStats/getEmptyStateHeadline";
import { NearbyBookingStatsCard } from "../NearbyBookingStats/NearbyBookingStatsCard";
import { ListViewBanners } from "./Banners";
import { useLastCheckedTimestamp } from "./useLastCheckedTimestamp";
import { WorkplaceReferralCta } from "./WorkplaceReferralCta";

interface ContextualEmptyStateProps {
  readonly daysCount: number;
  readonly inAppAd?: InferedInAppAdAttributesSchema;
  readonly workerPaySurveyId?: string;
  readonly children?: React.ReactNode;
  readonly onClearFilters: () => void;
  readonly unfilteredShiftsLength: number;
  readonly appliedFiltersCount: number;
  readonly shiftStartRelativeDaysGte: number;
  readonly shiftStartRelativeDaysLte: number;
}

export function ContextualEmptyState(props: ContextualEmptyStateProps) {
  const {
    inAppAd,
    workerPaySurveyId,
    children,
    onClearFilters,
    unfilteredShiftsLength,
    appliedFiltersCount,
    shiftStartRelativeDaysGte,
    shiftStartRelativeDaysLte,
  } = props;

  const { formattedTimestamp, refresh: refreshTimestamp } = useLastCheckedTimestamp();
  const workerGeoLocation = useWorkerGeoLocation();
  const { data: workerQualifications, isSuccess: qualificationsFetched } =
    useWorkerQualifications();
  const workerQualificationNames = (workerQualifications ?? []).map(
    (qualification) => qualification.attributes.name
  );

  const workerLatitude = workerGeoLocation?.latitude;
  const workerLongitude = workerGeoLocation?.longitude;

  const queryParameters = useMemo(() => {
    if (!(isDefined(workerLatitude) && isDefined(workerLongitude))) {
      return undefined;
    }

    const now = new Date();
    return {
      filter: {
        status: "booked" as const,
        "shift.start": {
          gte: addDays(now, shiftStartRelativeDaysGte).toISOString(),
          lte: addDays(now, shiftStartRelativeDaysLte).toISOString(),
        },
        location: {
          latitude: workerLatitude,
          longitude: workerLongitude,
          distance: DEFAULT_DISTANCE_IN_MILES_FILTER,
        },
      },
      fields: {
        "shift-stats": "totalCount,qualificationCounts" as const,
      },
    };
  }, [workerLatitude, workerLongitude, shiftStartRelativeDaysGte, shiftStartRelativeDaysLte]);

  const shiftsStatsQuery = useGetShiftsStats(queryParameters, {
    enabled: isDefined(queryParameters) && qualificationsFetched,
  });

  useEffect(() => {
    if (shiftsStatsQuery.dataUpdatedAt > 0) {
      refreshTimestamp();
    }
  }, [shiftsStatsQuery.dataUpdatedAt, refreshTimestamp]);

  const bookingStatsCardProps =
    shiftsStatsQuery.data && !shiftsStatsQuery.isError
      ? getBookingStatsCardProps({
          qualificationCounts: shiftsStatsQuery.data.data.attributes.qualificationCounts ?? [],
          totalCount: shiftsStatsQuery.data.data.attributes.totalCount ?? 0,
          workerQualifications: workerQualificationNames,
        })
      : undefined;

  const headline = bookingStatsCardProps
    ? getEmptyStateHeadline(bookingStatsCardProps, workerQualificationNames[0])
    : "No shifts in your area";

  useLogEffect(
    APP_V2_USER_EVENTS.EMPTY_STATE_BOOKING_STATS_VIEWED,
    {
      totalCount: shiftsStatsQuery.data?.data.attributes.totalCount,
      qualificationCount: shiftsStatsQuery.data?.data.attributes.qualificationCounts?.length,
      headline,
    },
    { enabled: Boolean(bookingStatsCardProps) }
  );

  const showResetFilters = appliedFiltersCount > 0 && unfilteredShiftsLength > 0;

  return (
    <Box sx={{ flex: "1", overflow: "auto", pb: 10 }}>
      <ListViewBanners />
      <Stack sx={{ flex: "1", p: 5, pt: 10 }} justifyContent="center" spacing={5}>
        <Stack spacing={1} alignItems="center">
          <Title semibold variant="h4" component="h3" sx={{ textAlign: "center" }}>
            {headline}
          </Title>
          <Text
            variant="body2"
            color={(theme) => theme.palette.text.secondary}
            data-testid="last-checked-timestamp"
          >
            {formattedTimestamp}
          </Text>
        </Stack>

        {showResetFilters && (
          <Button
            variant="contained"
            size="medium"
            sx={{ width: "fit-content", alignSelf: "center" }}
            onClick={onClearFilters}
          >
            Reset filters to see {unfilteredShiftsLength} shifts
          </Button>
        )}

        <Stack spacing={3} sx={{ pt: 4 }}>
          {shiftsStatsQuery.isLoading && (
            <Skeleton
              variant="rounded"
              height={120}
              sx={{ mx: 5, borderRadius: 3 }}
              data-testid="booking-stats-skeleton"
            />
          )}

          {bookingStatsCardProps && <NearbyBookingStatsCard {...bookingStatsCardProps} />}

          <WorkplaceReferralCta />

          {children}

          {inAppAd && <InAppAdCard inAppAd={inAppAd} sx={{ mx: 5 }} />}
          {workerPaySurveyId && <WorkerPaySurveyCard surveyId={workerPaySurveyId} sx={{ mx: 5 }} />}
        </Stack>
      </Stack>
    </Box>
  );
}
